import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {ObjectState} from 'types/commons'
import {
  DropboxUploadPreviewTabType,
  DropboxUploadPreviewTabViewState,
} from 'types/modules'
import {getBorder} from 'utils/commons'

interface StyledTabItemContainerProps {
  isSelected: boolean
  isDisabled: boolean
}

const StyledContainer = styled.div`
  ${({theme}) => ({
    background: theme.white_1,
    borderBottom: getBorder(1, 'solid', theme.gray_1),
  })}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${convertUnit(46)};
`

const StyledTabItem = styled.div<StyledTabItemContainerProps>`
  ${({theme, isSelected, isDisabled}) => ({
    height: isSelected ? `calc(100% - 26px)` : undefined,
    color: isSelected
      ? theme.primary_5
      : isDisabled
      ? theme.gray_3
      : theme.gray_6,
    borderBottom: getBorder(
      2,
      'solid',
      isSelected ? theme.primary_5 : 'transparent',
    ),
    ':hover': {
      cursor: isDisabled ? 'default' : 'pointer',
      opacity: isDisabled ? 'unset' : 0.7,
    },
  })}
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: ${convertUnit(12)};
`

const StyledLabel = styled(Paragraph)`
  color: inherit;
  text-align: center;
`

export default function TemplateDropboxTabView({
  stateView,
  duplicate,
  failed,
}: {
  stateView: ObjectState<DropboxUploadPreviewTabViewState>
  duplicate: File[]
  failed: File[]
}) {
  const [view, setView] = stateView
  const {tab} = view

  const handleTabChange = useCallback(
    (key: DropboxUploadPreviewTabType) => {
      setView((prev) => ({
        ...prev,
        tab: key,
      }))
    },
    [setView],
  )

  const tabItems = useMemo(
    () => [
      {
        key: 'failed' as DropboxUploadPreviewTabType,
        label: 'Failed',
        isDisabled: failed.length === 0,
        isSelected: tab === 'failed',
      },
      {
        key: 'duplicate' as DropboxUploadPreviewTabType,
        label: 'Duplicates',
        isDisabled: duplicate.length === 0,
        isSelected: tab === 'duplicate',
      },
    ],
    [failed.length, duplicate.length, tab],
  )

  return (
    <StyledContainer>
      {tabItems.map(({key, label, isDisabled, isSelected}) => (
        <StyledTabItem
          key={key}
          isDisabled={isDisabled}
          isSelected={isSelected}
          onClick={() => {
            if (!isDisabled && !isSelected) {
              handleTabChange(key)
            }
          }}>
          <StyledLabel fontSize="m" fontWeight="bold">
            {label}
          </StyledLabel>
        </StyledTabItem>
      ))}
    </StyledContainer>
  )
}
